// ════════════════════════════════════════════════════════════════════════════
//  PideAvisos.jsx
//  EL ÚNICO MOMENTO EN QUE SE LE PIDE PERMISO AL ALUMNO
//
//  ── POR QUÉ AQUÍ Y NO AL ENTRAR ────────────────────────────────────────────
//  Porque **un «no» del navegador es definitivo**. Chrome no vuelve a
//  preguntar nunca, ni a los tres días ni a los tres meses. Pedirlo a alguien
//  que acaba de llegar y no sabe qué es NSL no falla: quema la posibilidad para
//  siempre, incluida la de recordarle su clase tres semanas después, que es
//  justo el aviso que le habría evitado faltar.
//
//  Así que se pide UNA vez, y en el mejor momento que tiene NSL: **al terminar
//  su primera lección**. Ahí acaba de sentir que la app le sirve, hay una pausa
//  natural, y la pregunta se explica sola.
//
//  (El plan original era pedirlo tras reservar su primera clase. Al ir a
//  montarlo salió que en NSL el alumno NO reserva: las clases las asigna
//  gerencia desde el CRM, así que ese momento no existe.)
//
//  ── LAS CUATRO VECES QUE ESTO NO SE ENSEÑA ─────────────────────────────────
//  Y son la mitad del componente, porque enseñarlo de más es lo que lo estropea:
//
//    · Si el navegador no puede (o no hay service worker: en desarrollo, o en
//      un iPhone sin la app instalada).
//    · Si ya dijo que sí. No hay nada que pedir.
//    · Si ya dijo que no. El navegador no va a volver a preguntar, así que un
//      botón aquí sería un botón que no hace nada.
//    · Si ya se le preguntó y lo dejó pasar. Una vez es una oferta; dos es
//      insistir, y a la tercera se apaga la app entera.
//
//  ── Y NO INTERRUMPE ────────────────────────────────────────────────────────
//  No es un cartel encima ni un modal. Es una tarjeta más al final del cierre
//  de la lección, con su «ahora no» al lado y del mismo tamaño. Rob ya corrigió
//  esto una vez —«me bloqueó todo el sistema… nunca pude responder»— y la
//  lección vale igual aquí: lo que interrumpe se cierra sin leer.
// ════════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../api/apiClient";
import "./PideAvisos.css";

const LLAVE = "nsl_avisos_ofrecido";

const SOPORTADO = typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

function yaSeOfrecio() {
  try { return localStorage.getItem(LLAVE) === "1"; } catch { return false; }
}

function apunta() {
  try { localStorage.setItem(LLAVE, "1"); } catch { /* modo privado */ }
}

function aBytes(base64url) {
  const relleno = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const crudo = window.atob(base64);
  const bytes = new Uint8Array(crudo.length);
  for (let i = 0; i < crudo.length; i += 1) bytes[i] = crudo.charCodeAt(i);
  return bytes;
}

function nombreDelAparato() {
  const ua = navigator.userAgent || "";
  const sistema = /Android/i.test(ua) ? "Android"
    : /iPhone|iPad/i.test(ua) ? "iPhone"
      : /Windows/i.test(ua) ? "Windows"
        : /Mac/i.test(ua) ? "Mac" : "Aparato";
  const instalada = window.matchMedia?.("(display-mode: standalone)").matches;
  return `${sistema}${instalada ? " (app instalada)" : " (navegador)"}`;
}

export default function PideAvisos() {
  const [fase, setFase] = useState("mirando");
  const [clave, setClave] = useState("");

  const mira = useCallback(async () => {
    //  El orden importa: lo barato primero. Si ya se ofreció o el permiso está
    //  decidido, no se llama al servidor para nada.
    if (!SOPORTADO || yaSeOfrecio()) { setFase("no"); return; }
    if (Notification.permission !== "default") { setFase("no"); return; }

    const registro = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((ok) => setTimeout(() => ok(null), 5000)),
    ]);
    if (!registro) { setFase("no"); return; }

    //  Y si ya tiene una suscripción viva —porque lo encendió desde su panel—
    //  tampoco hay nada que ofrecer.
    if (await registro.pushManager.getSubscription()) { setFase("no"); return; }

    try {
      const { data } = await apiClient.get("/api/push/clave-publica");
      if (!data?.configurado) { setFase("no"); return; }
      setClave(data.clave_publica || "");
      setFase("ofrecer");
    } catch {
      setFase("no");
    }
  }, []);

  useEffect(() => { mira(); }, [mira]);

  const acepta = async () => {
    setFase("pidiendo");
    //  Se apunta ANTES de preguntar, no después. Si se apuntara al terminar y
    //  el alumno cierra la pestaña con el diálogo del navegador abierto, la
    //  próxima lección se lo volvería a ofrecer — y eso, para él, es insistir.
    apunta();
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== "granted") { setFase("no"); return; }

      const registro = await navigator.serviceWorker.ready;
      let suscripcion = await registro.pushManager.getSubscription();
      if (!suscripcion) {
        suscripcion = await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: aBytes(clave),
        });
      }
      const bruto = suscripcion.toJSON();
      await apiClient.post("/api/push/suscribir", {
        endpoint: bruto.endpoint,
        keys: { p256dh: bruto.keys.p256dh, auth: bruto.keys.auth },
        aparato: nombreDelAparato(),
      });
      setFase("listo");
    } catch {
      setFase("no");
    }
  };

  const ahoraNo = () => { apunta(); setFase("no"); };

  if (fase === "no" || fase === "mirando") return null;

  if (fase === "listo") {
    return (
      <div className="pav-caja pav-listo" data-prueba="pide-avisos-listo">
        <span className="pav-icono" aria-hidden="true">✅</span>
        <p className="pav-texto">
          Hecho. Te avisaremos de tu clase y de tu plan. Puedes apagarlo cuando
          quieras desde tu panel.
        </p>
      </div>
    );
  }

  return (
    <div className="pav-caja" data-prueba="pide-avisos">
      <span className="pav-icono" aria-hidden="true">🔔</span>
      <div className="pav-cuerpo">
        <p className="pav-titulo">¿Te avisamos de tu próxima clase?</p>
        {/* Qué SÍ y qué NO, en una línea. Es lo que convierte una petición de
            permiso en una decisión informada — y lo que hace que esto sea un
            aviso de servicio y no publicidad disfrazada. */}
        <p className="pav-texto">
          Te recordamos tu clase y te avisamos si tu plan está por terminar.
          Nada de promociones, y lo apagas cuando quieras.
        </p>
        <div className="pav-botones">
          <button type="button" className="pav-boton pav-si"
                  data-prueba="pide-avisos-si"
                  onClick={acepta} disabled={fase === "pidiendo"}>
            {fase === "pidiendo" ? "Un momento…" : "Sí, avísame"}
          </button>
          <button type="button" className="pav-boton pav-no"
                  data-prueba="pide-avisos-no" onClick={ahoraNo}>
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
}
